/** @format */

import React from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
	Alert,
	Snackbar,
	Portal,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store/store";
import { selectCircleData } from "../../redux/selectors/circleSelector";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import {
	endCycle,
	getCurrentCycleOfCircle,
	startCycle,
} from "../../redux/actions/contributionAction";
import { buttonColors, colors } from "../../utility/types/constant";
import { LoadingSpinner } from "../allComponents/AllComponents";
import { selectCurrentCycleOfCircle } from "../../redux/selectors/contributionSelector";
import moment from "moment";

declare module "@mui/material/styles" {
	interface PaletteColor {
		800?: string;
	}
	interface SimplePaletteColorOptions {
		800?: string;
	}

	interface TypographyVariants {
		commonAvatar: React.CSSProperties;
		smallAvatar: React.CSSProperties;
		mediumAvatar: React.CSSProperties;
		largeAvatar: React.CSSProperties;
	}

	// Allow configuration using `createTheme`
	interface TypographyVariantsOptions {
		commonAvatar?: React.CSSProperties;
		smallAvatar?: React.CSSProperties;
		mediumAvatar: React.CSSProperties;

		largeAvatar?: React.CSSProperties;
	}
}

const ContributionCycle = () => {
	const dispatch = useDispatch<AppDispatch>();
	const circleData = useSelector(selectCircleData);
	const currentUser = useSelector(selectCurrentUser);
	// const currentCycle = useSelector(selectCurrentCycleOfCircle);

	const currentCycle = useSelector((state: any) =>
		selectCurrentCycleOfCircle(state, circleData?._id ?? "")
	);

	const isLeader = circleData?.leader?._id === currentUser?._id;

	const [open, setOpen] = useState(false);
	const [cycleStartDate, setCycleStartDate] = useState("");
	const [, setPayOutDate] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [alert, setAlert] = useState<{
		type: "success" | "error";
		message: string;
	} | null>(null);
	const [openAlert, setOpenAlert] = useState(false);

	useEffect(() => {
		if (circleData?._id && currentCycle?._id) {
			dispatch(getCurrentCycleOfCircle({ unionId: circleData?._id }));
		}
	}, [dispatch, circleData?._id, currentCycle?._id]);

	const handleClickOpen = () => {
		setOpen(true);
		setError("");
		setCycleStartDate("");
	};

	const handleClose = () => {
		setOpen(false);
		setCycleStartDate("");
		setPayOutDate("");
		setError("");
	};

	const handleSnackbarClose = (event?: any, reason?: string) => {
		if (reason === "clickaway") return;
		setOpenAlert(false);
	};

	const handleSubmit = async () => {
		setError("");

		if (!cycleStartDate) {
			setError("Cycle start date is required");
			return;
		}

		const startUtc = moment.utc(cycleStartDate, "YYYY-MM-DD", true);

		if (!startUtc.isValid()) {
			setError("Invalid cycle start date");
			return;
		}
		// const payOutDateObj = moment.utc(payOutDate, "YYYY-MM-DD").toDate();

		const todayUTCStart = moment.utc().startOf("day");

		// validate cycle start date: must be today or later and not in the past
		if (startUtc.isBefore(todayUTCStart)) {
			setError("Cycle start date cannot be in the past");
			return;
		}

		const startDateObj = startUtc.toDate();

		setLoading(true);

		try {
			const payload = {
				cycleStartDate: startDateObj,
				// payOutDate: payOutDateObj,
			};

			const result = await dispatch(
				startCycle({
					unionId: circleData?._id ?? "",
					payload,
				})
			).unwrap();

			if (result && result._id) {
				setAlert({
					type: "success",
					message: "Contribution cycle started successfully!",
				});
				setOpenAlert(true);
				handleClose();

				setTimeout(() => {
					if (!currentCycle) {
						dispatch(
							getCurrentCycleOfCircle({
								unionId: circleData?._id ?? "",
							})
						);
					}
				}, 900);
			} else {
				setAlert({
					type: "error",
					message:
						"Failed to start contribution cycle. Cycle start date and payout date must be one month apart",
				});
				setOpenAlert(true);
				handleClose();
			}
		} catch (error: any) {
			setAlert({
				type: "error",
				message: error.message || "Failed to start contribution cycle.",
			});
			setOpenAlert(true);
			handleClose();
		} finally {
			setLoading(false);
		}
	};

	// const formattedStartDate =
	// 	currentCycle &&
	// 	currentCycle.startDate &&
	// 	!isNaN(new Date(currentCycle.startDate).getTime())
	// 		? moment.utc(currentCycle.startDate).format("YYYY-MM-DD")
	// 		: cycleStartDate;

	const formattedStartDate = currentCycle?.startDate
		? moment.utc(currentCycle.startDate).format("YYYY-MM-DD")
		: cycleStartDate || ""; // keep blank until user selects

	// const formattedPayOutDate =
	// 	currentCycle &&
	// 	currentCycle.payOutDate &&
	// 	!isNaN(new Date(currentCycle.payOutDate).getTime())
	// 		? moment.utc(currentCycle.payOutDate).format("YYYY-MM-DD")
	// 		: payOutDate;

	const today = moment.utc().format("YYYY-MM-DD");

	const isPayOutDate = currentCycle?.payOutDate
		? today === moment.utc(currentCycle.payOutDate).format("YYYY-MM-DD")
		: false;

	const handleEndCycle = async () => {
		setLoading(true);
		try {
			const result = await dispatch(
				endCycle({
					cycleId: currentCycle?._id ?? "",
					unionId: circleData?._id ?? "",
				})
			).unwrap();

			if (result && result.cycle && result.cycle._id) {
				setAlert({
					type: "success",
					message: "Contribution cycle ended successfully!",
				});
				setOpenAlert(true);
			} else {
				setAlert({
					type: "error",
					message: "Failed to end contribution cycle",
				});
				setOpenAlert(true);
			}
		} catch (error: any) {
			setAlert({
				type: "error",
				message: error.message || "Failed to end contribution cycle",
			});
			setOpenAlert(true);
		} finally {
			setLoading(false);
		}
	};

	return (
		// <CardWrapper border={false} content={false}>
		<React.Fragment>
			{isLeader && (
				<Box sx={{ p: 2 }}>
					<Button
						size="large"
						variant="contained"
						onClick={handleClickOpen}
						sx={{
							bgcolor: buttonColors.primary,
							"&:hover": {
								bgcolor: buttonColors.primary,
							},
						}}>
						Start Contribution Cycle
					</Button>

					{isPayOutDate && (
						<Button
							size="large"
							variant="outlined"
							onClick={handleEndCycle}
							sx={{
								bgcolor: "transparent",
								"&:hover": {
									bgcolor: "transparent",
									borderColor: buttonColors.secondaryMain,
								},
								margin: "0 1rem",
								borderColor: buttonColors.secondaryMain,
								color: buttonColors.primaryDark,
							}}>
							{loading ? (
								<LoadingSpinner loading={loading} color={colors.blueRibbon} />
							) : (
								"End Contribution Cycle"
							)}
						</Button>
					)}
					<Dialog open={open} onClose={handleClose} fullWidth>
						<DialogTitle>Start Contribution Cycle</DialogTitle>
						<DialogContent>
							{currentCycle && (
								<Typography
									variant="body2"
									color={colors.warning}
									sx={{ mb: 2, fontSize: "1.1rem", fontWeight: "bold" }}>
									Contribution cycle already initiated. The start and payout
									dates will be enabled once the current cycle is over.
								</Typography>
							)}
							{error && (
								<Typography
									variant="body2"
									color="error"
									sx={{ mb: 2, fontSize: "1.6rem", fontWeight: "bold" }}>
									{error}
								</Typography>
							)}
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 2,
									mt: 1,
								}}>
								<TextField
									key={open ? "date-open" : "date-closed"}
									label="Cycle Start Date"
									type="date"
									InputLabelProps={{ shrink: true }}
									value={cycleStartDate || ""}
									onChange={(e) => setCycleStartDate(e.target.value)}
									fullWidth
									disabled={!!currentCycle}
									autoComplete="off"
									name="cycleStartDate_noautofill"
									// inputProps={{
									// 	min: moment.utc().format("YYYY-MM-DD"),
									// }}
								/>
								{/* <TextField
                  label="Payout Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formattedPayOutDate}
                  onChange={(e) => setPayOutDate(e.target.value)}
                  fullWidth
                  disabled={!!currentCycle}
                /> */}
							</Box>
						</DialogContent>
						<DialogActions>
							<Button
								size="large"
								onClick={handleClose}
								disabled={loading}
								sx={{
									color: buttonColors.persianRed,
									fontSize: "1rem",
									fontWeight: "bold",
								}}>
								Cancel
							</Button>
							<Button
								size="large"
								onClick={handleSubmit}
								variant="outlined"
								disabled={loading || !!currentCycle}
								sx={{
									borderColor: buttonColors.mainPrimary,
								}}>
								<Typography
									variant="subtitle2"
									sx={{
										color: buttonColors.primaryDark,
										fontSize: "1rem",
										fontWeight: "bold",
									}}>
									{loading ? (
										<LoadingSpinner
											loading={loading}
											color={colors.blueRibbon}
										/>
									) : (
										"Start Cycle"
									)}
								</Typography>
							</Button>
						</DialogActions>
					</Dialog>
				</Box>
			)}
			<Portal>
				<Snackbar
					open={openAlert}
					autoHideDuration={15000}
					onClose={handleSnackbarClose}
					anchorOrigin={{ vertical: "top", horizontal: "center" }}
					sx={{
						top: "50% !important",
						transform: "translateY(-50%)",
						zIndex: 999999,
						position: "fixed",
						"& .MuiSnackbar-root": {
							zIndex: 999999,
						},
					}}>
					{alert ? (
						<Alert
							onClose={handleSnackbarClose}
							variant="filled"
							severity={alert.type}
							sx={{
								width: "100%",
								minHeight: "100px",
								fontSize: "1.4rem",
								padding: "20px",
								display: "flex",
								alignItems: "center",
								zIndex: 1000000,
								boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
								position: "relative",
							}}>
							{alert.message}
						</Alert>
					) : (
						<></>
					)}
				</Snackbar>
			</Portal>
		</React.Fragment>
		// </CardWrapper>
	);
};

export default ContributionCycle;
